import React from 'react';
import { 
  Send, 
  AlertCircle,
  Clock
} from 'lucide-react';

const MessageStatus = ({ status, timestamp, responseText, showTooltip = true }) => {
  // Configurações para cada status
  const statusConfig = {
    sent: {
      icon: Send,
      color: 'text-gray-500',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
      label: 'Enviada',
      description: 'Mensagem foi enviada'
    },
    failed: {
      icon: AlertCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
      label: 'Falhou',
      description: 'Falha no envio'
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      label: 'Pendente',
      description: 'Aguardando envio'
    }
  };

  const config = statusConfig[status] || statusConfig.sent;
  const IconComponent = config.icon;

  // Formatar timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  // Componente do ícone
  const StatusIcon = () => (
    <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${config.bgColor}`}>
      <IconComponent className={`w-3 h-3 ${config.color}`} />
    </div>
  );

  // Componente do tooltip
  const TooltipContent = () => (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg max-w-xs">
      <div className="font-medium">{config.label}</div>
      <div className="text-gray-300 mt-1">{config.description}</div>
      {timestamp && (
        <div className="text-gray-400 mt-1 text-xs">
          {formatTimestamp(timestamp)}
        </div>
      )}
      {responseText && status === 'responded' && (
        <div className="text-gray-300 mt-2 border-t border-gray-700 pt-2">
          <div className="font-medium text-xs">Resposta:</div>
          <div className="text-xs mt-1 break-words">
            "{responseText.length > 100 ? responseText.substring(0, 100) + '...' : responseText}"
          </div>
        </div>
      )}
    </div>
  );

  if (!showTooltip) {
    return <StatusIcon />;
  }

  return (
    <div className="relative group">
      <StatusIcon />
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
        <TooltipContent />
        {/* Seta do tooltip */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

// Componente para mostrar múltiplos status em linha
export const MessageStatusLine = ({ statuses = [], className = '' }) => {
  if (!statuses || statuses.length === 0) {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <MessageStatus status="sent" showTooltip={false} />
        <span className="text-xs text-gray-500 ml-2">Enviada</span>
      </div>
    );
  }

  // Ordenar status por hierarquia
  const statusOrder = ['sent', 'failed'];
  const sortedStatuses = statuses.sort((a, b) => {
    return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
  });

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {sortedStatuses.map((statusItem, index) => (
        <MessageStatus
          key={index}
          status={statusItem.status}
          timestamp={statusItem.timestamp}
          responseText={statusItem.responseText}
          showTooltip={true}
        />
      ))}
    </div>
  );
};

// Componente para mostrar status resumido com texto
export const MessageStatusSummary = ({ status, timestamp, responseText, className = '' }) => {
  const statusConfig = {
    sent: { label: 'Enviada', color: 'text-gray-600' },
    failed: { label: 'Falhou', color: 'text-red-600' },
    pending: { label: 'Pendente', color: 'text-yellow-600' }
  };

  const config = statusConfig[status] || statusConfig.sent;

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <MessageStatus status={status} timestamp={timestamp} responseText={responseText} />
      <span className={`text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
      {timestamp && (
        <span className="text-xs text-gray-500">
          {new Date(timestamp).toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </span>
      )}
    </div>
  );
};

export default MessageStatus;